import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import subscriptionService, {
  SubscriptionDetails,
  Plan,
} from '../../services/subscriptionService';
import { hasPermission, Permission } from '../../constants/permissions';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const SubscriptionManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [error, setError] = useState('');
  const [daysRemaining, setDaysRemaining] = useState(0);

  // Check permission
  if (!hasPermission(user, Permission.SUBSCRIPTIONS_VIEW)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view subscriptions.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sub, availablePlans, days] = await Promise.all([
        subscriptionService.getCurrentSubscription(),
        subscriptionService.getAvailablePlans(),
        subscriptionService.getDaysUntilExpiry(),
      ]);
      setSubscription(sub);
      setPlans(availablePlans);
      setDaysRemaining(days);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = (plan: string) => {
    // Navigate to upgrade flow
    navigate(`/subscription/upgrade?plan=${plan}`);
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    try {
      await subscriptionService.cancelSubscription({
        immediate: false,
      });
      alert('Subscription cancelled successfully. It will remain active until the end of your billing period.');
      fetchData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to cancel subscription');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-sm text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return null;
  }

  const statusColors = {
    active: 'bg-green-100 text-green-800',
    grace_period: 'bg-yellow-100 text-yellow-800',
    expired: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800',
  };

  const planColors = {
    trial: 'bg-gray-100 text-gray-800 border-gray-300',
    starter: 'bg-blue-100 text-blue-800 border-blue-300',
    professional: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    enterprise: 'bg-purple-100 text-purple-800 border-purple-300',
  };

  const isExpired = subscription.status === 'expired' || subscription.status === 'cancelled';
  const isGracePeriod = subscription.status === 'grace_period';
  const isTrial = subscription.currentPlan === 'trial';

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
        <p className="mt-2 text-gray-600">Manage your subscription plan and billing</p>
      </div>

      {/* Warning Banners */}
      {isExpired && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Subscription Expired</h3>
              <p className="mt-1 text-sm text-red-700">
                Your subscription has expired. Please upgrade to continue using all features.
              </p>
            </div>
          </div>
        </div>
      )}

      {isGracePeriod && (
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Grace Period</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Your subscription has expired but you have {daysRemaining} days remaining in your grace period.
                Please renew to avoid losing access.
              </p>
            </div>
          </div>
        </div>
      )}

      {isTrial && daysRemaining > 0 && daysRemaining <= 7 && (
        <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-blue-400 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">Trial Ending Soon</h3>
              <p className="mt-1 text-sm text-blue-700">
                Your free trial ends in {daysRemaining} days. Upgrade now to continue using all features.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Subscription Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Subscription</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Plan</p>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${planColors[subscription.currentPlan]}`}>
              {subscriptionService.formatPlanName(subscription.currentPlan)}
            </span>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-1">Status</p>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${statusColors[subscription.status]}`}>
              {subscription.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-1">End Date</p>
            <p className="text-base font-medium text-gray-900">
              {new Date(subscription.endDate).toLocaleDateString()}
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-1">Days Remaining</p>
            <p className="text-base font-medium text-gray-900">{daysRemaining} days</p>
          </div>
        </div>

        {subscription.autoRenew && (
          <div className="mt-4 flex items-center text-sm text-green-600">
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            <span>Auto-renewal enabled</span>
          </div>
        )}
      </div>

      {/* Usage Limits Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Plan Limits</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Users</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {subscription.limits.maxUsers === -1 ? '∞' : subscription.limits.maxUsers}
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Warehouses</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {subscription.limits.maxWarehouses === -1 ? '∞' : subscription.limits.maxWarehouses}
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Inventory Items</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {subscription.limits.maxInventoryItems === -1 ? '∞' : subscription.limits.maxInventoryItems}
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Purchase Orders / Month</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {subscription.limits.maxPurchaseOrdersPerMonth === -1 ? '∞' : subscription.limits.maxPurchaseOrdersPerMonth}
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600">Retail Bills / Month</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {subscription.limits.maxRetailBillsPerMonth === -1 ? '∞' : subscription.limits.maxRetailBillsPerMonth}
            </p>
          </div>
        </div>
      </div>

      {/* Features Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Enabled Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {subscription.features.map((feature) => (
            <div key={feature} className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-sm text-gray-700">
                {subscriptionService.formatFeatureName(feature)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Available Plans */}
      {(isTrial || isExpired) && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans
              .filter((plan) => plan.plan !== 'trial')
              .map((plan) => (
                <div
                  key={plan.plan}
                  className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6 hover:border-indigo-500 transition-colors"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {subscriptionService.formatPlanName(plan.plan)}
                  </h3>
                  <div className="mb-4">
                    <p className="text-3xl font-bold text-gray-900">
                      {subscriptionService.formatCurrency(plan.pricing.monthly)}
                      <span className="text-sm font-normal text-gray-600">/month</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      or {subscriptionService.formatCurrency(plan.pricing.annual)}/year
                    </p>
                  </div>

                  <ul className="space-y-2 mb-6">
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">
                        {plan.limits.maxUsers === -1 ? 'Unlimited' : plan.limits.maxUsers} Users
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">
                        {plan.limits.maxWarehouses === -1 ? 'Unlimited' : plan.limits.maxWarehouses} Warehouses
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">
                        {plan.limits.maxInventoryItems === -1 ? 'Unlimited' : plan.limits.maxInventoryItems} Inventory Items
                      </span>
                    </li>
                  </ul>

                  {hasPermission(user, Permission.SUBSCRIPTIONS_MANAGE) && (
                    <button
                      onClick={() => handleUpgrade(plan.plan)}
                      className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                    >
                      Upgrade to {subscriptionService.formatPlanName(plan.plan)}
                    </button>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Subscription History */}
      {subscription.subscriptionHistory.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription History</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subscription.subscriptionHistory.map((history) => (
                  <tr key={history._id}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(history.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {subscriptionService.formatPlanName(history.plan)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{history.transactionType}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {subscriptionService.formatCurrency(history.amount, history.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        history.transactionStatus === 'success' ? 'bg-green-100 text-green-800' :
                        history.transactionStatus === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {history.transactionStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {history.invoiceNumber ? (
                        <a href="#" className="text-indigo-600 hover:text-indigo-800">
                          {history.invoiceNumber}
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Actions */}
      {subscription.status === 'active' && !isTrial && (
        <div className="mt-6 flex justify-end">
          {hasPermission(user, Permission.SUBSCRIPTIONS_MANAGE) && (
            <button
              onClick={handleCancelSubscription}
              className="px-6 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50"
            >
              Cancel Subscription
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
